import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Compass, ChevronDown, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";

// PATCH 39 — BeCo Index panel. Deliberately a DIFFERENT visual treatment from CRX (segmented bar +
// alpha/sigma split), its own panel, never rendered adjacent to CRX. H3 drill-down expands the ten
// named sub-metrics in plain language (never the internal aB,i / sB,j codes). H5 subjective feedback
// is shown as a visibly separate kind of signal, never merged into this measured number.
export default function BecoPanel({ testid = "beco-panel" }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/coach/beco/me").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;

  const alpha = d.sub_metrics.filter((s) => s.family === "alpha");
  const sigma = d.sub_metrics.filter((s) => s.family === "sigma");

  return (
    <div className="k-container" data-testid={testid} style={{ padding: 22, marginBottom: 16, borderLeft: "3px solid var(--accent-primary)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Compass size={16} aria-hidden="true" />
        <h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>BeCo Index</h3>
        <span className="k-badge" data-testid="beco-band" style={{ marginLeft: "auto", fontSize: 11 }}>{d.band}</span>
      </div>

      {/* Headline — a segmented bar, distinct from CRX's dial treatment. */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 12 }}>
        <span className="k-heading" data-testid="beco-index-value" style={{ fontSize: 40, fontWeight: 700, letterSpacing: "-.02em" }}>{d.index}</span>
        <span className="k-muted" style={{ fontSize: 12 }}>/ 100 · behavioural build discipline</span>
      </div>
      <div style={{ display: "flex", gap: 3, marginTop: 8 }} aria-hidden="true">
        {Array.from({ length: 20 }).map((_, i) => (
          <span key={i} style={{ flex: 1, height: 8, borderRadius: 2,
            background: i < Math.round(d.index / 5) ? "var(--accent-primary)" : "var(--control)" }} />
        ))}
      </div>
      <p className="k-muted" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.6 }}>{d.methodology}</p>
      <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 11.5 }} className="k-muted">
        <span>Momentum (α): <strong className="k-heading">{d.synthesis.alpha_raw}</strong></span>
        <span>Scope drag (σ): <strong className="k-heading">{d.synthesis.sigma_raw}</strong></span>
        <span>Confidence: <strong className="k-heading">{Math.round(d.synthesis.confidence * 100)}%</strong></span>
      </div>

      {/* H3 — "tell me why" drill-down into the ten named sub-metrics, in plain language. */}
      <button className="k-btn k-btn-ghost" data-testid="beco-drilldown-toggle" onClick={() => setOpen((o) => !o)}
        style={{ marginTop: 14, padding: "6px 10px", fontSize: 12 }}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Tell me why
      </button>
      {open && (
        <div data-testid="beco-submetrics" style={{ marginTop: 12 }}>
          {[["Momentum", alpha, ArrowUpRight], ["Scope drag", sigma, ArrowDownRight]].map(([title, list, Icon]) => (
            <div key={title} style={{ marginBottom: 12 }}>
              <div className="k-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 6 }}>{title}</div>
              {list.map((s) => (
                <div key={s.key} data-testid={`beco-sub-${s.key}`} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon size={13} aria-hidden="true" />
                    <span className="k-heading" style={{ fontSize: 13 }}>{s.label}</span>
                    <span className="k-muted" style={{ fontSize: 12 }}> · {s.plain_label}</span>
                    <span className="k-muted" style={{ marginLeft: "auto", fontSize: 12 }}>
                      {s.cold_start ? "gathering data" : s.value}
                    </span>
                  </div>
                  <p className="k-muted" style={{ fontSize: 11.5, margin: "4px 0 0", lineHeight: 1.55 }}>{s.plain}</p>
                  {s.citation && <p className="k-muted" style={{ fontSize: 10.5, margin: "2px 0 0", opacity: 0.8 }}>Grounding: {s.citation}</p>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <p className="k-muted" data-testid="beco-subjective" style={{ fontSize: 11, marginTop: 12, paddingTop: 10, borderTop: "1px dashed var(--border-strong)" }}>
        {d.subjective_note}
      </p>
      <p className="k-muted" data-testid="beco-footnote" style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85 }}>*{d.patent_footnote}</p>
    </div>
  );
}
